import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { WORKFLOW_TEMPLATES_REFERENCE_DATA_TYPES, WorkflowTemplatesReferenceData } from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { ApplicationState } from "@idw-types/state/application-state";
import { getWorkflowTemplatesReduxRequest } from "@redux-requests/workflow-templates";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AddWorkflowTemplateModal } from "@containers/modals/add-workflow-template-modal/add-workflow-template-modal";
import { WorkflowTemplatesTableContainer } from "./workflow-templates-table-container/workflow-templates-table-container";

export const WorkflowTemplatesContainer = () => {
    const modalContext = useModalContext()

    const dispatch = useDispatch();
    const {
        isLoadingWorkflowTemplates,
        workflowTemplates,
        workflowTemplatesTableState
    } = useSelector((state: ApplicationState) => ({
        isLoadingWorkflowTemplates: state.workflowTemplates.data.isLoading,
        workflowTemplates: state.workflowTemplates.data.workflowTemplates,
        workflowTemplatesTableState: state.tableSettings.workflowTemplates
    }));

    const referenceData = useReferenceDataSelector<WorkflowTemplatesReferenceData>(WORKFLOW_TEMPLATES_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadWorkflowTemplates, setShouldLoadWorkflowTemplates] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.workflowTemplates && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                WORKFLOW_TEMPLATES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.WORKFLOW_TEMPLATES
            );
        }
    }, [shouldLoadReferenceData, referenceData.isLoading.workflowTemplates]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies
        );
    }, [
        referenceData.axnCompanies
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingWorkflowTemplates && shouldLoadWorkflowTemplates) {
            setShouldLoadWorkflowTemplates(false);
            getWorkflowTemplatesReduxRequest(dispatch)(referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingWorkflowTemplates, shouldLoadWorkflowTemplates]);

    const renderAddWorkflowTemplate = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddWorkflowTemplateModal
                onModalClose={onModalClose}
                axnCompanies={referenceData.axnCompanies}
            />
        );
    }, [referenceData.axnCompanies]);

    const isLoading = useMemo(() => {
        return (
            isLoadingWorkflowTemplates ||
            referenceData.isLoading.workflowTemplates
        );
    }, [
        isLoadingWorkflowTemplates,
        referenceData.isLoading.workflowTemplates
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            workflowTemplates &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadWorkflowTemplates
        );
    }, [
        isLoading,
        workflowTemplates,
        shouldLoadWorkflowTemplates,
        shouldLoadReferenceData,
        isReferenceDataPresent
    ]);


    const headerButton = useMemo(() => {
        const onClick = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddWorkflowTemplate,
                renderFunction: renderAddWorkflowTemplate
            });
        };
        return (
            <ButtonWithIcon
                id="add-workflow-template-button"
                title={resources.pages.workflowTemplates.headerButton}
                text={resources.pages.workflowTemplates.headerButton}
                onClick={onClick}
                icon={faPlusCircle}
                disabled={isLoading || !isLoaded}
            />
        );
    }, [isLoading, isLoaded, renderAddWorkflowTemplate]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.workflowTemplates.header}
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <WorkflowTemplatesTableContainer
                        workflowTemplates={workflowTemplates}
                        tableState={workflowTemplatesTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};